import React from 'react';
import { Route } from 'react-router-dom';

// Oncology Module Components
import OncologyDashboard from '../components/oncology/OncologyDashboard';
import CancerDiagnosisForm from '../components/oncology/CancerDiagnosisForm';
import CancerDiagnosisHistory from '../components/oncology/CancerDiagnosisHistory';
import ChemotherapyRegimens from '../components/oncology/ChemotherapyRegimens';
import ChemotherapyRegimenForm from '../components/oncology/ChemotherapyRegimenForm';
import ChemotherapyCycleTracker from '../components/oncology/ChemotherapyCycleTracker';
import DoseCalculator from '../components/oncology/DoseCalculator';
import PerformanceStatusAssessment from '../components/oncology/PerformanceStatusAssessment';
import ToxicityAssessment from '../components/oncology/ToxicityAssessment';
import ToxicityList from '../components/oncology/ToxicityList';
import TreatmentPlanBuilder from '../components/oncology/TreatmentPlanBuilder';
import TreatmentPlanList from '../components/oncology/TreatmentPlanList';
import TreatmentTimeline from '../components/oncology/TreatmentTimeline';
import TumorBoardScheduler from '../components/oncology/TumorBoardScheduler';
import TumorBoardMeeting from '../components/oncology/TumorBoardMeeting';
import ClinicalTrialSearch from '../components/oncology/ClinicalTrialSearch';
import ClinicalTrialForm from '../components/oncology/ClinicalTrialForm';
import TrialEnrollment from '../components/oncology/TrialEnrollment';
import SurvivorshipCarePlan from '../components/oncology/SurvivorshipCarePlan';
import SurvivorshipList from '../components/oncology/SurvivorshipList';
import FollowUpScheduler from '../components/oncology/FollowUpScheduler';
import EpisodeOfCareManager from '../components/oncology/EpisodeOfCareManager';
import EpisodeOfCarePage from '../components/oncology/EpisodeOfCarePage';

/**
 * Oncology Module Routes Configuration
 * 
 * Routes structure:
 * - /oncology - Main dashboard
 * - /oncology/diagnosis/new/:patientId - New cancer diagnosis
 * - /oncology/diagnosis/edit/:diagnosisId - Edit diagnosis
 * - /oncology/diagnosis/history/:patientId - Diagnosis history
 * - /oncology/chemotherapy/regimens - Regimen library
 * - /oncology/chemotherapy/tracker/:patientId - Cycle tracker
 * - /oncology/chemotherapy/dose-calculator/:patientId - Dose calculator
 * - /oncology/treatment-plan/new/:patientId - New treatment plan
 * - /oncology/treatment-plan/edit/:planId - Edit treatment plan
 * - /oncology/treatment-timeline/:patientId - Treatment timeline
 * - /oncology/assessments/performance/:patientId - Performance status
 * - /oncology/assessments/toxicity/:patientId - Toxicity assessment
 * - /oncology/tumor-board/scheduler - Schedule meetings
 * - /oncology/tumor-board/meeting/:boardId - Meeting view
 * - /oncology/clinical-trials/search - Trial search
 * - /oncology/clinical-trials/enrollment/:patientId/:trialId? - Enrollment
 * - /oncology/survivorship/plan/:patientId - Survivorship plan
 * - /oncology/survivorship/follow-up/:patientId - Follow-up scheduler
 */

const OncologyRouteConfig = [
  // Dashboard
  {
    path: 'oncology',
    element: <OncologyDashboard />,
    roles: ['admin', 'doctor', 'nurse', 'facility_admin']
  },
  // Cancer Diagnosis - List all diagnoses
  {
    path: 'oncology/diagnoses',
    element: <CancerDiagnosisHistory />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Cancer Diagnosis - New (select patient first)
  {
    path: 'oncology/diagnosis/new',
    element: <CancerDiagnosisForm />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/diagnosis/new/:patientId',
    element: <CancerDiagnosisForm />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/diagnosis/edit/:diagnosisId',
    element: <CancerDiagnosisForm />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/diagnosis/history/:patientId',
    element: <CancerDiagnosisHistory />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Chemotherapy list (from dashboard)
  {
    path: 'oncology/chemotherapy',
    element: <ChemotherapyRegimens />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/chemotherapy/new',
    element: <ChemotherapyRegimenForm />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/chemotherapy/schedule',
    element: <ChemotherapyCycleTracker />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Chemotherapy
  {
    path: 'oncology/chemotherapy/regimens',
    element: <ChemotherapyRegimens />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/chemotherapy/tracker/:patientId',
    element: <ChemotherapyCycleTracker />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/chemotherapy/dose-calculator/:patientId',
    element: <DoseCalculator />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Clinical Assessments
  {
    path: 'oncology/assessments/performance/:patientId',
    element: <PerformanceStatusAssessment />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/assessments/toxicity/:patientId',
    element: <ToxicityAssessment />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Toxicity - List view (from dashboard/navigation)
  {
    path: 'oncology/toxicity',
    element: <ToxicityList />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Toxicity - New record
  {
    path: 'oncology/toxicity/new',
    element: <ToxicityAssessment />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Toxicity - Detail view
  {
    path: 'oncology/toxicity/:recordId',
    element: <ToxicityAssessment />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Treatment Planning list (from dashboard - facility-wide)
  {
    path: 'oncology/treatment-plans',
    element: <TreatmentPlanList />,
    roles: ['admin', 'doctor']
  },
  // Treatment Planning list (patient-specific)
  {
    path: 'oncology/treatment-plans/:patientId',
    element: <TreatmentPlanList />,
    roles: ['admin', 'doctor']
  },
  {
    path: 'oncology/treatment-plan/new',
    element: <TreatmentPlanBuilder />,
    roles: ['admin', 'doctor']
  },
  // Treatment Planning
  {
    path: 'oncology/treatment-plan/new/:patientId',
    element: <TreatmentPlanBuilder />,
    roles: ['admin', 'doctor']
  },
  {
    path: 'oncology/treatment-plan/edit/:planId',
    element: <TreatmentPlanBuilder />,
    roles: ['admin', 'doctor']
  },
  // Treatment Plan View (read-only)
  {
    path: 'oncology/treatment-plan/view/:planId',
    element: <TreatmentPlanBuilder />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/treatment-timeline/:patientId',
    element: <TreatmentTimeline />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Tumor Board list (from dashboard)
  {
    path: 'oncology/tumor-board',
    element: <TumorBoardScheduler />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/tumor-board/schedule',
    element: <TumorBoardScheduler />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  // Tumor Board
  {
    path: 'oncology/tumor-board/scheduler',
    element: <TumorBoardScheduler />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/tumor-board/meeting/:boardId',
    element: <TumorBoardMeeting />,
    roles: ['admin', 'doctor', 'nurse', 'facility_admin']
  },
  // Clinical Trials list (from dashboard)
  {
    path: 'oncology/clinical-trials',
    element: <ClinicalTrialSearch />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Clinical Trials
  {
    path: 'oncology/clinical-trials/search',
    element: <ClinicalTrialSearch />,
    roles: ['admin', 'doctor', 'nurse']
  },
  {
    path: 'oncology/clinical-trials/new',
    element: <ClinicalTrialForm />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/clinical-trials/edit/:trialId',
    element: <ClinicalTrialForm />,
    roles: ['admin', 'doctor', 'facility_admin']
  },
  {
    path: 'oncology/clinical-trials/enrollment/:patientId/:trialId?',
    element: <TrialEnrollment />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Survivorship - List view (from dashboard/navigation)
  {
    path: 'oncology/survivorship',
    element: <SurvivorshipList />,
    roles: ['admin', 'doctor']
  },
  // Survivorship - New plan
  {
    path: 'oncology/survivorship/new',
    element: <SurvivorshipCarePlan />,
    roles: ['admin', 'doctor']
  },
  // Survivorship - Detail view by plan ID
  {
    path: 'oncology/survivorship/:planId',
    element: <SurvivorshipCarePlan />,
    roles: ['admin', 'doctor']
  },
  // Survivorship - Patient-specific (legacy)
  {
    path: 'oncology/survivorship/plan/:patientId',
    element: <SurvivorshipCarePlan />,
    roles: ['admin', 'doctor']
  },
  {
    path: 'oncology/survivorship/follow-up/:patientId',
    element: <FollowUpScheduler />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Episode of Care - Facility-wide list
  {
    path: 'oncology/episodes',
    element: <EpisodeOfCarePage />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Episode of Care - View specific episode timeline
  {
    path: 'oncology/episodes/:episodeId',
    element: <EpisodeOfCarePage />,
    roles: ['admin', 'doctor', 'nurse']
  },
  // Episode of Care - Patient-specific view
  {
    path: 'oncology/episodes/patient/:patientId',
    element: <EpisodeOfCareManager />,
    roles: ['admin', 'doctor', 'nurse']
  }
];

// Generate Route components for use in router
export const oncologyRoutes = OncologyRouteConfig.map(route => (
  <Route
    key={route.path}
    path={route.path}
    element={route.element}
  />
));

// Export routes configuration for use in role-based access control
export const getOncologyRouteConfig = () => OncologyRouteConfig;

export default OncologyRouteConfig;


